import React, { useState, useEffect } from "react";
import { Grid, Tooltip, IconButton } from "@material-ui/core";
import Icon from "@jll/icon";
import { mlHttpPost } from "../../../../API/Http";
import TextField from "../../../../Component/Forms/TextField";
import SearchableSelect from "../../../../Component/Forms/SearchableSelect";
import { LoadingIndicator } from "../../../../Component";
import { useTranslation } from "react-i18next";
import DateField from "../../../../Component/Forms/DateField";
require("dotenv").config();

const { REACT_APP_ENABLE_DOCUMENT_TYPE_DETECTION } = process.env || {};

const ONE_MB = 1024 * 1024;
export default function UploadForm({
  classes,
  file,
  fileIndex,
  handleChange,
  documentTypes,
  handleRemove,
  hasDocType,
  hasExpiryDate = false,
  expirationDate,
  selectedType,
  errors = {},
}) {
  const detectDocumentType =
    parseInt(REACT_APP_ENABLE_DOCUMENT_TYPE_DETECTION, 10) === 1 || false;
  const { t } = useTranslation();
  const [updatedfileName, setUpdatedFileName] = useState(file.name);
  const [documentType, setDocumentType] = useState(
    documentTypes.find((dt) => dt.value === selectedType),
  );
  const [detectedDocumentType, setDetectedDocumentType] = useState(null);

  const options = [
    {
      value: false,
      label: t("labels.common.no"),
    },
    {
      value: true,
      label: t("labels.common.yes"),
    },
  ];

  const [hasExpiry, setHasExpiry] = useState(
    options.find(({ value }) => value === hasExpiryDate),
  );
  const [expiryDate, setExpiryDate] = useState(expirationDate);
  const [gettingDocType, setGettingDocType] = useState(false);
  const [isPredictionDone, setIsPredictionDone] = useState(hasDocType);
  const [predictionData, setPredictionData] = useState(null);

  useEffect(() => {
    handleChange(fileIndex, {
      updatedfileName,
      documentType: documentType ? documentType.value : null,
      hasExpiry: hasExpiry ? hasExpiry.value : null,
      expiryDate: hasExpiry && hasExpiry.value ? expiryDate : null,
      isPredictionDone: isPredictionDone,
    });
  }, [updatedfileName, documentType, hasExpiry, expiryDate, isPredictionDone]);

  useEffect(() => {
    const getDocumentType = async () => {
      setGettingDocType(true);
      try {
        const formData = new FormData();
        formData.append("file", file);

        const results = await mlHttpPost(
          "/v1/document/classification-and-extraction",
          formData,
        );

        const { data = {} } = results;
        if (data) {
          const { is_orbis, is_crefo, is_passport } = data;
          let dt;

          if (is_crefo) dt = documentTypes.find((dt) => dt.value === "10");
          else if (is_orbis) dt = documentTypes.find((dt) => dt.value === "9");
          else if (is_passport)
            dt = documentTypes.find((dt) => dt.value === "15");

          setDetectedDocumentType(dt);
          setPredictionData(data);
        }
      } catch (err) {
        console.log(err);
      }
      setIsPredictionDone(true);
      setGettingDocType(false);
    };

    if (
      detectDocumentType &&
      file &&
      file.type === "application/pdf" &&
      !isPredictionDone
    ) {
      getDocumentType();
    }
  }, [file, detectDocumentType]);

  const selectDocTypeSuggestion = (event) => {
    event.preventDefault();
    if (predictionData) {
      const { is_passport, extracted_data } = predictionData;
      if (is_passport) {
        setHasExpiry({
          value: true,
          label: t("labels.common.yes"),
        });

        if (extracted_data["expiry_date"]) {
          setExpiryDate(extracted_data["expiry_date"]);
        }
      }
    }
    setDocumentType(detectedDocumentType);
    setDetectedDocumentType(null);
  };

  return (
    <Grid container spacing={3} justify="space-between">
      <Grid item md={11}>
        <Grid container spacing={3} justify="flex-start">
          <Grid item md={3}>
            <TextField
              label={t("labels.record.documents.form.fileName")}
              type="text"
              required={true}
              initialValue={updatedfileName}
              //disabled={true}
              onChange={(e) => setUpdatedFileName(e)}
            />
            {errors["filename"] && (
              <div className={classes.errorText}>{errors["filename"]}</div>
            )}
          </Grid>
          <Grid item md={3}>
            <SearchableSelect
              options={documentTypes}
              label={t("labels.record.documents.form.documentType")}
              value={documentType}
              changeHandler={setDocumentType}
              required={true}
              initialValue={documentType}
            />
            {!gettingDocType && errors["documentType"] && (
              <div className={classes.errorText}>{errors["documentType"]}</div>
            )}
            {gettingDocType && (
              <div className={classes.infoText}>
                {t("labels.record.documents.form.suggestionsLoading")}
              </div>
            )}
            {!gettingDocType && detectedDocumentType != null && (
              <div className={classes.infoText}>
                {t("labels.record.documents.form.suggestedType")}:{" "}
                <a
                  className={classes.infoText}
                  href="#"
                  onClick={selectDocTypeSuggestion}
                >
                  {detectedDocumentType.label}
                </a>
              </div>
            )}
          </Grid>
          <Grid item md={2}>
            <TextField
              label={t("labels.record.documents.form.size")}
              type="text"
              required={true}
              initialValue={Math.round((file.size / ONE_MB) * 100) / 100}
              disabled={true}
            />
            {errors["size"] && (
              <div className={classes.errorText}>{errors["size"]}</div>
            )}
          </Grid>
          <Grid item md={2}>
            <SearchableSelect
              options={options}
              label={t("labels.record.documents.form.hasExpiryDate")}
              value={hasExpiry}
              changeHandler={setHasExpiry}
              required={true}
              disableClearable
            />
          </Grid>
          {hasExpiry && hasExpiry.value && (
            <Grid item md={2}>
              <DateField
                label={t("labels.record.documents.form.documentExpiryDate")}
                isRequired={true}
                initialValue={expiryDate}
                onChange={setExpiryDate}
              />
              {errors["expiryDate"] && (
                <div className={classes.errorText}>{errors["expiryDate"]}</div>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid item md={1}>
        <Tooltip title={t("labels.common.remove")} placement="bottom">
          <IconButton
            onClick={(event) => handleRemove(fileIndex)}
            style={{ fontSize: 16, marginTop: 25 }}
          >
            <Icon className="far fa-trash-alt" />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
}
